import cn from 'classnames';
import LeftArrowIcon from '@/shared/assets/icons/education/Chevron_Left.svg';
import RightArrowIcon from '@/shared/assets/icons/education/Chevron_Right.svg';
import { Svg } from '@/shared/ui/Svg';
import type { IContentStep } from '@/shared/types/lesson';
import { getLessonStepTitle } from './stepTheme';

type LessonStepPagerProps = {
  steps: IContentStep[];
  currentStepOrder: number;
  onChangeStep: (order: number) => void;
};

const navCardClassName = (enabled: boolean) =>
  cn(
    'flex h-[77px] w-full max-w-[343px] shrink-0 items-center gap-4 rounded-2xl bg-white p-4 text-left shadow-sm transition',
    enabled ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-50',
  );

export const LessonStepPager = ({
  steps,
  currentStepOrder,
  onChangeStep,
}: LessonStepPagerProps) => {
  const sorted = [...steps].sort((a, b) => a.order - b.order);
  const index = sorted.findIndex((step) => step.order === currentStepOrder);
  const current = sorted[index] ?? sorted[0];
  const prev = index > 0 ? sorted[index - 1] : null;
  const next = index >= 0 && index < sorted.length - 1 ? sorted[index + 1] : null;

  if (!current) return null;

  return (
    <div className="flex flex-col items-stretch gap-3 lg:flex-row lg:gap-4">
      <button
        type="button"
        disabled={!prev}
        onClick={() => prev && onChangeStep(prev.order)}
        className={cn(navCardClassName(Boolean(prev)), 'lg:flex-none')}
      >
        <Svg Icon={LeftArrowIcon} className="h-5 w-5 shrink-0 text-grey-secondary" />
        <div className="min-w-0">
          <p className="text-base font-normal leading-[140%] text-black">
            Step {prev?.order ?? current.order}
          </p>
          <p className="truncate text-base font-medium leading-[120%] text-black">
            {getLessonStepTitle(prev ?? current)}
          </p>
        </div>
      </button>

      <div className="flex min-h-[77px] flex-1 items-center justify-center overflow-x-auto rounded-2xl bg-grey-10 px-3 py-2">
        <div className="inline-flex items-center gap-1" role="group" aria-label="Lesson steps">
          {sorted.map((step) => {
            const isCurrent = step.order === current.order;
            return (
              <button
                key={step.id}
                type="button"
                aria-current={isCurrent ? 'step' : undefined}
                title={getLessonStepTitle(step)}
                onClick={() => onChangeStep(step.order)}
                className={cn(
                  'flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-base font-normal leading-[140%] transition',
                  isCurrent
                    ? 'border border-[#211D55] bg-white text-black'
                    : 'border border-transparent text-grey-50 hover:bg-white hover:text-black',
                )}
              >
                {step.order}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        disabled={!next}
        onClick={() => next && onChangeStep(next.order)}
        className={cn(navCardClassName(Boolean(next)), 'lg:ml-auto lg:flex-none')}
      >
        <div className="min-w-0 flex-1">
          <p className="text-base font-normal leading-[140%] text-black">
            Step {next?.order ?? current.order}
          </p>
          <p className="truncate text-base font-medium leading-[120%] text-black">
            {getLessonStepTitle(next ?? current)}
          </p>
        </div>
        <Svg Icon={RightArrowIcon} className="h-5 w-5 shrink-0 text-grey-secondary" />
      </button>
    </div>
  );
};
